// reports-view.js — CLIENT-SIDE shaping, raw FHIR -> the buildReportsView DTO
// (server/opd/view-adapter.mjs:2341 + listAllReports, state-machine.mjs:715),
// computed from a DocumentReference search with the Encounter+Patient fanned
// in via _include.
//
// The server's per-row `canRead` chart-read-gate re-check (C5) is dropped —
// same reasoning as patients-view.js/schedule-view.js: RLS already scoped
// this DocumentReference search to what the doctor may read, so there is
// nothing left to re-check app-side.
import { formatClinicTime, formatInClinicTz } from '../clinic-time.js';
import { ageFromBirthDate, nameOf, initials } from '../fhir-person.js';

const MRN_SYS = 'https://healther.io/opd/mrn';
const OPD_STATE = 'https://healther.io/opd/state';

const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
// clinic-tz via clinic-time.js, 2026-08-05 W2.1 — was device-tz toLocaleDateString.
const fmtDate = (iso) => formatInClinicTz(iso, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
const fmtTime = (iso) => formatClinicTime(iso); // clinic wall-clock, not viewer device tz

function fileKindFromMime(mime) {
  const m = String(mime || '').toLowerCase();
  if (!m) return 'file';
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('audio/')) return 'audio';
  if (m === 'application/pdf' || m.includes('pdf')) return 'pdf';
  if (m.startsWith('video/')) return 'video';
  return 'file';
}

export function groupReportsBundle(bundle) {
  const encounters = {}; const patients = {}; const docs = [];
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    if (r.resourceType === 'Encounter') encounters['Encounter/' + r.id] = r;
    else if (r.resourceType === 'Patient') patients['Patient/' + r.id] = r;
    else if (r.resourceType === 'DocumentReference') docs.push(r);
  }
  return { encounters, patients, docs };
}

export function shapeReportRow(doc, encounters, patients) {
  const encRef = doc.context && doc.context.encounter && doc.context.encounter[0] && doc.context.encounter[0].reference;
  const enc = encRef ? encounters[encRef] : null;
  const patientRef = doc.subject && doc.subject.reference;
  const patient = patientRef ? patients[patientRef] : null;
  const name = patient ? nameOf(patient) : 'Patient';
  const mrn = patient && (patient.identifier || []).find((i) => i.system === MRN_SYS);
  const stateExt = enc && ext(enc, OPD_STATE);
  const att = (doc.content && doc.content[0] && doc.content[0].attachment) || {};
  const code = (doc.type && doc.type.coding && doc.type.coding[0] && doc.type.coding[0].code) || 'document';
  return {
    id: doc.id,
    encounterId: enc ? enc.id : null,
    encounterState: stateExt ? stateExt.valueString : null,
    // Patient UUID for consultUrl's ?patient= on-open auto-grant (only when the
    // patient resolved via _include). Named *MedplumId to match the slug/_medplumId
    // convention — this is the FHIR UUID, not the OPD slug.
    patientMedplumId: patient ? patient.id : null,
    patientName: name,
    patientInitials: initials(name),
    mrn: mrn ? mrn.value : '',
    age: ageFromBirthDate(patient && patient.birthDate),
    sex: (patient && patient.gender) ? patient.gender[0].toUpperCase() : '',
    fileType: fileKindFromMime(att.contentType),
    docType: code,
    title: att.title || code,
    url: att.url || null,
    contentType: att.contentType || '',
    uploadedAt: doc.date || (doc.meta && doc.meta.lastUpdated) || null,
    uploadedAtLabel: doc.date ? `${fmtDate(doc.date)} · ${fmtTime(doc.date)}` : '',
  };
}

// The most-recent encounter-linked consult for a patient's file rows, or null.
// Rows arrive newest-first within a patient (server _sort=patient,-date; and the
// fallback buildReportsView sorts each group's rows -date implicitly by insert
// order), so the FIRST row carrying an encounterId is the latest openable
// consult. `encounterId` is set ONLY when the linked Encounter was fanned in via
// _include, i.e. RLS actually returned it — a patient-uploaded file with no
// consult, or a doc whose encounter the doctor can't read, contributes nothing.
// Returns { encounterId, patientMedplumId } for consultUrl's on-open auto-grant,
// or null when the patient has no openable consult (header omits the affordance).
export function latestConsultOf(rows) {
  const r = (rows || []).find((x) => x && x.encounterId);
  return r ? { encounterId: r.encounterId, patientMedplumId: r.patientMedplumId || null } : null;
}

export function buildReportsView(rows) {
  const byPatient = new Map();
  for (const r of rows) {
    const key = r.mrn || r.patientName;
    if (!byPatient.has(key)) byPatient.set(key, { key, patientName: r.patientName, mrn: r.mrn, initials: r.patientInitials, age: r.age, sex: r.sex, rows: [] });
    byPatient.get(key).rows.push(r);
  }
  const groups = Array.from(byPatient.values()).sort((a, b) => String((b.rows[0] && b.rows[0].uploadedAt) || '').localeCompare(String((a.rows[0] && a.rows[0].uploadedAt) || '')));
  for (const g of groups) g.latestConsult = latestConsultOf(g.rows);
  // No generatedAt: the payload must be deterministic for a given data state —
  // the SWR layer diffs cached vs fresh by JSON equality, and a per-call
  // timestamp would make every revalidate look like a change.
  return {
    rows, groups,
    counts: {
      total: rows.length,
      pdf: rows.filter((r) => r.fileType === 'pdf').length,
      image: rows.filter((r) => r.fileType === 'image').length,
      audio: rows.filter((r) => r.fileType === 'audio').length,
      patients: groups.length,
    },
  };
}
